/**
 * ⭐ **`centvoxFetch` — TEK HTTP GEÇİDİ** (Rapor 20 §1.3).
 *
 * Orval'in ürettiği tüm hook'lar bu mutator'dan geçer. Merkezîleştirdikleri:
 *
 * | Konu | Neden burada? |
 * |------|---------------|
 * | `Authorization` | Access token **yalnız bellekte** tutulur; `localStorage`'a YAZILMAZ (Rapor 03 §3.4) |
 * | `Idempotency-Key` | Yazma uçlarında zorunlu (Rapor 04 §8) |
 * | **402 ≠ 403** | 402 "planını yükselt", 403 "yetkin yok" — farklı UI (Rapor 13 §6.1) |
 * | 401 + `token.refresh_required` | Tek seferlik yenileme ve isteği tekrarlama |
 * | 429 geri çekilme | `Retry-After` başlığına saygı |
 * | `AbortSignal` | TanStack Query iptalleri |
 * | **URL temizleme** | Telemetriye giden URL'den sır/kimlik ayıklanır (Rapor 14 §9.3) |
 */

import { API_ORIGIN } from "../app/bootstrap.js";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId: string,
    readonly requiredPlan?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** ⭐ 402: kullanıcıya "yükselt" gösterilir. */
  get isPlanLimit(): boolean {
    return this.status === 402;
  }

  /** ⭐ 403: kullanıcıya "bu işlemi yapamazsın" gösterilir. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

/** Access token **yalnız bellekte**. Sayfa yenilenirse refresh cookie'siyle alınır. */
let accessToken: string | null = null;
let refreshing: Promise<void> | null = null;

/**
 * ⭐ **CSRF jetonu — bellekte.**
 *
 * ### 🐞 Neden gerekti?
 * Jeton `document.cookie`'den okunuyordu. İstemci Pages'te (`*.pages.dev`),
 * API ise Worker'da (`*.workers.dev`): çerez **API alan adına** kurulduğu için
 * istemcinin JS'i onu **hiçbir zaman göremez**. `csrfToken()` her seferinde
 * boş dönüyor, sunucudaki çift gönderim kontrolü düşüyor ve
 * `POST /token/refresh` **her zaman** `malformed_token` veriyordu.
 *
 * Artık sunucu jetonu yanıt gövdesinde de gönderiyor; burada bellekte tutulur.
 *
 * ⚠️ `localStorage`'a YAZILMAZ: access token'la aynı kural (Rapor 03 §3.4).
 */
let csrf: string | null = null;

/**
 * ⭐ **Yenileme jetonu — bellekte, yalnız çerez gelmediğinde kullanılır.**
 *
 * 🚨 `centvox_rt` çerezi API alan adına aittir; kullanıcı istemcinin alan
 *    adındadır. Yani çerez **üçüncü taraftır** ve tarayıcılar onu giderek
 *    daha çok engelliyor. Ölçülen sonuç: `/token/refresh` → `malformed_token`,
 *    ardından erişim jetonu düşüyor ve istemci saniyede bir bilet+yenileme
 *    denemesine giriyordu ("Oturumunuz sona erdi").
 *
 * ⚠️ `localStorage`'a YAZILMAZ: erişim jetonuyla aynı kural. Sayfa yenilenince
 *    kaybolur — o durumda çerez varsa o çalışır, yoksa kullanıcı yeniden katılır.
 */
let refreshToken: string | null = null;

/**
 * ⭐ **OTURUM KURTARILAMAZ — TERMİNAL DURUM.**
 *
 * 🚨 Yenileme başarısız olduğunda istemcinin **duracağı bir nokta yoktu**:
 *    bilet isteği 401 alıyor, yenileme deneniyor, o da 401 alıyor, soket
 *    yeniden bağlanmayı kuruyor ve döngü baştan başlıyordu. Canlı kayıtta
 *    bu, iki dakika boyunca saniyede ~2 istek olarak sürdü ve hiçbir zaman
 *    sona ermedi.
 *
 * Sayfa yenilendikten sonra bu durum **kaçınılmazdır**: erişim jetonu, CSRF
 * ve yenileme jetonu yalnız bellektedir (Rapor 03 §3.4) ve çapraz-site çerez
 * de engellenmişse geriye hiçbir kimlik kalmaz. Doğru davranış sonsuz denemek
 * değil, kullanıcıya **yeniden katıl** demektir.
 */
let sessionDead = false;

/** ⚠️ Soket ve denetleyici buna bakıp yeniden denemeyi DURDURUR. */
export function isSessionDead(): boolean {
  return sessionDead;
}

/** Yeni bir katılımda durum sıfırlanır. */
export function clearSessionDead(): void {
  sessionDead = false;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/** ⚠️ `/join` ve `/token/refresh` yanıtlarındaki `csrfToken` buraya yazılır. */
export function setCsrfToken(token: string | null): void {
  csrf = token;
}

/** ⚠️ Aynı iki yanıttaki `refreshToken` buraya yazılır (bkz. yukarıdaki not). */
export function setRefreshToken(token: string | null): void {
  refreshToken = token;
}

export function getAccessToken(): string {
  return accessToken ?? "";
}

/**
 * Telemetriye giden URL'den **query string ve fragment tamamen** atılır.
 *
 * ⚠️ Fragment sunucuya gitmez ama istemci telemetrisi URL'i tümüyle gönderirse
 *    **davet sırrı sızar** (Rapor 03 §3.1'in "fragment'ın koruyamadıkları"
 *    listesi). Bu yüzden temizleme istemcide de yapılır.
 */
export function scrubUrl(raw: string): string {
  try {
    const u = new URL(raw, location.origin);
    u.search = "";
    u.hash = "";
    u.pathname = u.pathname
      .replace(/\/j\/[0-9a-f]{32}/g, "/j/:meetingId")
      .replace(/\/(meetings|rooms)\/[0-9a-f]{32}/g, "/$1/:meetingId");
    return u.toString();
  } catch {
    return "[invalid]";
  }
}

export interface CentvoxFetchOptions extends RequestInit {
  /** Yazma uçlarında zorunlu. */
  idempotencyKey?: string;
  /** 401 sonrası tek seferlik yenileme denendi mi? */
  _retried?: boolean;
  /**
   * ⚠️ Yalnız `centvoxFetchWithHeaders` kullanır; doğrudan çağrılmaz.
   *    Başlıkları dışarı taşımanın tek yolu budur — `Response` nesnesini
   *    döndürmek, gövdenin iki kez okunmasına açık kapı bırakırdı
   *    (`res.json()` bir kez çalışır).
   */
  __onHeaders?: (headers: Headers) => void;
}

/**
 * ⚠️ Göreli yollar **API köküne** çevrilir (Pages ↔ Worker ayrı origin).
 *    Mutlak URL'ler dokunulmadan geçer (Images yükleme URL'i gibi).
 */
export function apiUrl(url: string): string {
  return url.startsWith("http") ? url : `${API_ORIGIN}${url}`;
}

/**
 * ⭐ **Gövde + BAŞLIKLAR** — bazı uçlar bilgiyi başlıkta taşır.
 *
 * ⚠️ Ayrı bir fonksiyondur; `centvoxFetch`e `withHeaders` gibi bir bayrak
 *    eklemek dönüş tipini koşullu yapardı ve her çağıran, ihtiyacı olmadığı
 *    hâlde birlik tipini dar altmak zorunda kalırdı.
 *
 * ⚠️ Var olma sebebi somut: transkript ucu sayfalamayı `x-centvox-truncated`
 *    ve kapsamayı `x-centvox-coverage` **başlıklarında** bildirir. Gövdeye
 *    yazamaz — dosya biçimlerinin (VTT/MD) içine "devamı var" yazmak,
 *    oynatıcının onu içerik sanmasına yol açardı. Başlığı okumayan bir
 *    istemci, kesilmiş bir transkripti **tam sanır**.
 */
export async function centvoxFetchWithHeaders<T>(
  url: string,
  options: CentvoxFetchOptions = {},
): Promise<{ data: T; headers: Headers }> {
  let captured: Headers = new Headers();
  const data = await centvoxFetch<T>(url, {
    ...options,
    __onHeaders: (h: Headers) => {
      captured = h;
    },
  } as CentvoxFetchOptions);
  return { data, headers: captured };
}

export async function centvoxFetch<T>(url: string, options: CentvoxFetchOptions = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  if (options.idempotencyKey) headers.set("Idempotency-Key", options.idempotencyKey);
  /**
   * ⚠️ `FormData` **hariç tutulur**: multipart gövdenin `content-type`'ı
   *    boundary içerir ve onu yalnız tarayıcı üretebilir. Buraya
   *    `application/json` yazmak, sunucunun gövdeyi hiç ayrıştıramamasına yol
   *    açar — dosya yükleme sessizce `bad_payload` döndürürdü.
   */
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  if (options.body && !isFormData && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }

  /**
   * ⚠️ `credentials: "include"` — `same-origin` DEĞİL.
   *
   * Pages ile Worker ayrı origin'lerdedir; `same-origin` ile yenileme çerezi
   * hiç gönderilmez ve oturum uzatma sessizce başarısız olur.
   *
   * ⚠️ Sunucuda CORS `*` iken tarayıcı bu isteği **reddeder** — `*` ile
   *    kimlik bilgisi taşıyan istek birlikte kullanılamaz. Gerçek origin
   *    listesi girildiğinde çalışmaya başlar.
   */
  const res = await fetch(apiUrl(url), { ...options, headers, credentials: "include" });

  if (res.ok) {
    if (res.status === 204) return undefined as T;
    /**
     * 🚨 **200 + HTML = API ADRESİ YANLIŞ.**
     *
     * `res.json()` doğrudan çağrılıyordu. Paket `VITE_API_ORIGIN` olmadan
     * derlenirse istek Pages'in kendisine gider ve Pages, SPA geri dönüşü
     * olarak `index.html`'i **200 text/html** ile döndürür. `json()` o HTML
     * üzerinde `SyntaxError` fırlatır; bu hatanın `status` alanı YOKTUR,
     * dolayısıyla teşhis onu "ağ/CORS" kutusuna atar ve kullanıcı sorunu
     * Worker'ın CORS ayarında arar — yanlış katmanda.
     *
     * ⚠️ Ayrım burada yapılmalıdır: içerik türü JSON değilse bu bir ağ
     *    sorunu değil, bir **yapılandırma** sorunudur ve öyle söylenir.
     */
    const type = res.headers.get("content-type") ?? "";
    if (!type.includes("json")) {
      throw new ApiError(
        res.status,
        "api_origin_misconfigured",
        `API ${new URL(res.url).origin} adresinden JSON yerine "${type || "bilinmeyen tür"}" dondu. ` +
          "Paket dogru API kokuyle derlenmemis olabilir.",
        res.headers.get("x-request-id") ?? "-",
      );
    }
    options.__onHeaders?.(res.headers);
    return (await res.json()) as T;
  }

  // ── 401: bir kez yenile, sonra tekrar dene ───────────────────────────
  if (res.status === 401 && !options._retried) {
    await refreshOnce();
    return centvoxFetch<T>(url, { ...options, _retried: true });
  }

  const body = (await res.json().catch(() => null)) as {
    error?: { code?: string; message?: string; requestId?: string; requiredPlan?: string };
  } | null;

  /**
   * ── 429: `Retry-After`'a saygı — **ama her 429'a değil** ──────────────
   *
   * 🚨 Eskiden gövdeye bakılmadan **her** 429 bir kez yeniden deneniyordu.
   *    İki farklı şey aynı koda düşer ve doğru davranışları ZITTIR:
   *
   *    | Kod                  | Anlamı                   | Doğru davranış |
   *    |----------------------|--------------------------|----------------|
   *    | `rate_limited`       | çok hızlı gittin         | birazdan **tekrar dene** |
   *    | `ai_quota_exceeded`  | günlük AI kotası bitti   | **yarın** dene |
   *
   *    İkincisi **deterministik bir rettir**: iki saniye sonra tekrar denemek
   *    kesin başarısız olur. Bedeli boşuna değildir — asistan ucu o ikinci
   *    istekte `LIMIT 4000` segment sorgusunu baştan çalıştırır, yani D1 okuma
   *    kotası ve CPU, sonucu bilinen bir çağrı için harcanır. Kullanıcı da
   *    2 saniye boşuna "Düşünüyor…" görür.
   *
   * ⚠️ Gövde artık yeniden deneme kararından ÖNCE okunur; sıra bilinçlidir.
   *    `Response.json()` bir kez okunabilir, sonrasında gövde tüketilmiştir.
   */
  const code = body?.error?.code;
  if (res.status === 429 && !options._retried && code !== "ai_quota_exceeded") {
    const wait = Number(res.headers.get("Retry-After") ?? "2");
    await new Promise((r) => setTimeout(r, Math.min(wait, 10) * 1000));
    return centvoxFetch<T>(url, { ...options, _retried: true });
  }

  throw new ApiError(
    res.status,
    body?.error?.code ?? "unknown",
    body?.error?.message ?? "İstek başarısız oldu.",
    body?.error?.requestId ?? "-",
    body?.error?.requiredPlan,
  );
}

/** Eşzamanlı 401'lerde **tek** yenileme çağrısı yapılır. */
async function refreshOnce(): Promise<void> {
  if (refreshing) return refreshing;

  refreshing = (async () => {
    const res = await fetch(`${API_ORIGIN}/api/v1/token/refresh`, {
      method: "POST",
      // ⚠️ Çerez yine gönderilmeye ÇALIŞILIR: aynı kökenli dağıtımda (ortak
      //    alan adı) `HttpOnly` çerez en güvenli yoldur ve sunucu onu tercih eder.
      credentials: "include",
      headers: {
        "X-Centvox-Csrf": csrfToken(),
        // ⚠️ Yedek: çerez engellenmişse sunucu jetonu bu başlıktan okur.
        ...(refreshToken === null ? {} : { "X-Centvox-Refresh": refreshToken }),
      },
    });
    if (!res.ok) {
      accessToken = null;
      /**
       * ⚠️ **401 = kimlik yok, tekrar denemek anlamsız.** Diğer durum kodları
       *    (ağ hatası, 5xx) geçici olabilir; onlarda oturum ölü sayılmaz ve
       *    yeniden deneme hakkı korunur.
       */
      if (res.status === 401) sessionDead = true;
      throw new ApiError(res.status, "revoked", "Oturum sona erdi.", "-");
    }
    const data = (await res.json()) as {
      accessToken: string;
      csrfToken?: string;
      refreshToken?: string;
    };
    accessToken = data.accessToken;
    // ⚠️ Jeton her yenilemede DÖNER (çerez de döndürülür). Güncellenmezse
    //    bir sonraki yenileme eski değerle düşer ve oturum sessizce ölür.
    if (data.csrfToken) csrf = data.csrfToken;
    // ⚠️ Yenileme jetonu ROTASYONA uğrar; eski değeri saklamak, aile replay
    //    tespitini tetikleyip oturumu iptal ettirirdi.
    if (data.refreshToken) refreshToken = data.refreshToken;
    sessionDead = false;
  })().finally(() => {
    refreshing = null;
  });

  return refreshing;
}

/**
 * Çift gönderim CSRF token'ı — çerezle **birlikte** başlıkta da gönderilir.
 *
 * ⚠️ Token'ı istemci ÜRETMEZ: sunucu `/join` ve `/token/refresh` yanıtlarında
 *    kurar. İstemci üretseydi, çerezi hiç almamış bir sekme kendi uydurduğu
 *    değeri gönderir ve sunucudaki karşılaştırma her zaman başarısız olurdu.
 *
 * ⚠️ **Önce bellek, sonra çerez.** Çapraz kökende (Pages + Worker) çerez
 *    okunamaz; jeton yanıt gövdesinden gelir. Aynı kökene taşındığında çerez
 *    yolu hâlâ çalışır — bu yüzden ikisi de korunur, biri diğerinin yerine
 *    geçmez.
 *
 * ⚠️ İkisi de yoksa boş dönülür ve sunucu isteği **reddeder** — sessizce
 *    korumasız bir yola düşmek yerine açık bir hata.
 */
function csrfToken(): string {
  if (csrf !== null && csrf.length > 0) return csrf;
  const m = /(?:^|;\s*)centvox_csrf=([A-Za-z0-9_-]+)/.exec(document.cookie);
  return m?.[1] ?? "";
}
